import React, { Dispatch, useContext } from 'react';
import { PostScheduleViewContext } from '../PostSchedule.controller';
import { FilterGroupsContainer } from '../../../../components/containers/FilterGroupsContainer';
import { ButtonMenuContext } from '../../../../components/actions/ButtonMenu/ButtonMenu.view';
import { PostStatusFilterGroup } from '../../../../components/filters/PostStatusFilterGroup';
import { PostScheduleFilterAction, PostScheduleFilterType } from '../../../../models/post';
import { PostTimingFilterGroup } from '../../../../components/filters/PostTimingFilterGroup';
import { PostCategoryFilterGroup } from '../../../../components/filters/PostCategoryFilterGroup';
import { PostSocialProfilesFilterGroup } from '../../../../components/filters/PostSocialProfilesFilterGroup';
import { PostContentFilterGroup } from '../../../../components/filters/PostContentFilterGroup';
import { PostSourceFilterGroup } from '../../../../components/filters/PostSourceFilterGroup';
import { Search } from '../../../../components/forms/Search';
import { SubmitHandler, UseFormReturn } from 'react-hook-form/dist/types/form';
import {
  CoreCategoryFieldsFragment,
  PostQueueCountResult,
  PostTiming,
  QueueFilterPostEvergreen,
  QueueFilterPostStatus,
  SocialProfileFieldsFragment
} from '../../../../models/graphql';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { PostEvergreenFilterGroup } from '../../../../components/filters/PostEvergreenFilterGroup';
import { ActiveFilterGroup } from 'components/filters/ActiveFilterGroup';

export type PostScheduleFilterMenuViewProps = {
  filters: PostScheduleFilterType;
  context: PostScheduleViewContext;
  dispatchFilters: Dispatch<PostScheduleFilterAction>;
  categories: CoreCategoryFieldsFragment[];
  socialProfiles: SocialProfileFieldsFragment[];
  setSearchTerm: (term: string) => void;
  onSubmit: SubmitHandler<{ term: string }>;
  formInstance: UseFormReturn;
  queueCounts: PostQueueCountResult;
  isDropdown?: boolean;
  isLoading?: boolean;
};

export const PostScheduleFilterMenuView = (props: PostScheduleFilterMenuViewProps): JSX.Element => {
  const isFilterActive = Object.values(props.filters).some(f => !!f);
  const { openHandler } = useContext(ButtonMenuContext);
  const { formInstance, onSubmit } = props;
  const breakpoint = useBreakpoints();
  const shouldShowClearButton = isFilterActive && breakpoint !== 'small';

  const isLoading = props.isLoading || false;

  return (
    <FilterGroupsContainer
      showClearButton={shouldShowClearButton}
      isDropdown={!!props.isDropdown}
      reset={() => {
        props.setSearchTerm('');
        props.formInstance.setValue('term', '');
        props.dispatchFilters({ type: 'resetAll' });
      }}
      close={() => {
        openHandler(false);
      }}>
      <div className={'margin-bottom-small-24'}>
        <form onSubmit={formInstance.handleSubmit(() => onSubmit({ term: formInstance.getValues('term') }))}>
          <Search
            name={'term'}
            placeholder={'Search Posts'}
            action={() => {
              const term = props.formInstance.getValues('term');
              props.setSearchTerm(term);
              onSubmit({ term: formInstance.getValues('term') });
            }}
            formInstance={formInstance}
            onReset={() => {
              props.setSearchTerm('');
              props.formInstance.setValue('term', '');
              onSubmit({ term: formInstance.getValues('term') });
            }}
            rules={{}}
            isFilter={true}
          />
        </form>
      </div>
      <ActiveFilterGroup
        category={props.categories.find(c => c.id === props.filters.category)}
        profile={props.socialProfiles.find(p => p.id === props.filters.profile)}
        filters={props.filters}
        dispatchFilters={props.dispatchFilters}
        loading={isLoading}
      />
      <PostStatusFilterGroup
        loading={isLoading}
        dispatchFilters={props.dispatchFilters}
        context={props.context}
        activeFilterId={props.filters.status}
        queueCounts={props.queueCounts}
        filters={props.filters}
      />
      <PostSocialProfilesFilterGroup
        profiles={props.socialProfiles}
        loading={isLoading}
        dispatchFilters={props.dispatchFilters}
        activeFilterId={props.filters.profile}
        queueCounts={props.queueCounts}
      />
      <PostCategoryFilterGroup
        categories={props.categories}
        loading={isLoading}
        dispatchFilters={props.dispatchFilters}
        activeFilterId={props.filters.category}
        queueCounts={props.queueCounts}
      />
      {props.context !== PostScheduleViewContext.LIBRARY && (
        <>
          <PostTimingFilterGroup
            loading={isLoading}
            dispatchFilters={props.dispatchFilters}
            context={props.context}
            activeFilterId={props.filters.timing}
            evergreenSelected={props.filters.evergreen === QueueFilterPostEvergreen.EVERGREEN_ONLY}
            isUnqueueSelected={props.filters.status === QueueFilterPostStatus.UNQUEUED}
            queueCounts={props.queueCounts}
          />
          <PostEvergreenFilterGroup
            loading={isLoading}
            dispatchFilters={props.dispatchFilters}
            context={props.context}
            activeFilterId={props.filters.evergreen}
            recurringSelected={props.filters.timing === PostTiming.RECURRING}
            queueCounts={props.queueCounts}
          />
          <PostSourceFilterGroup
            loading={isLoading}
            dispatchFilters={props.dispatchFilters}
            context={props.context}
            activeFilterId={props.filters.source}
            queueCounts={props.queueCounts}
          />
          <PostContentFilterGroup
            loading={isLoading}
            dispatchFilters={props.dispatchFilters}
            context={props.context}
            activeFilterId={props.filters.content}
            queueCounts={props.queueCounts}
          />
        </>
      )}
    </FilterGroupsContainer>
  );
};
